import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Types
 */
export type ClassStatus = "active" | "paused" | "archived";

export interface ClassItem {
  id: string;
  name: string; // e.g. "Grade 10A"
  grade: string; // e.g. "Grade 10"
  teacher?: string;
  studentsCount: number;
  room?: string;
  schedule?: string; // e.g. "Mon-Fri 09:00-15:00"
  status?: ClassStatus;
}

interface ClassesState {
  classes: ClassItem[];
  loading: boolean;
  error: string | null;
}

/**
 * Initial state with sample data (replace with real data as needed)
 */
const initialState: ClassesState = {
  classes: [
    {
      id: "c1",
      name: "Grade 6A",
      grade: "Grade 6",
      teacher: "Ms. Priya Sharma",
      studentsCount: 36,
      room: "203",
      schedule: "Mon-Fri 09:00 - 15:00",
      status: "active",
    },
    {
      id: "c2",
      name: "Grade 7B",
      grade: "Grade 7",
      teacher: "Mr. Rahul Verma",
      studentsCount: 32,
      room: "204",
      schedule: "Mon-Fri 09:00 - 15:00",
      status: "active",
    },
    {
      id: "c3",
      name: "Grade 10A",
      grade: "Grade 10",
      teacher: "Mrs. Anita Joshi",
      studentsCount: 28,
      room: "302",
      schedule: "Mon-Fri 09:00 - 15:00",
      status: "paused",
    },
  ],
  loading: false,
  error: null,
};

/**
 * Slice
 */
const classesSlice = createSlice({
  name: "classes",
  initialState,
  reducers: {
    setClasses: (state, action: PayloadAction<ClassItem[]>) => {
      state.classes = action.payload;
    },

    addClass: (state, action: PayloadAction<ClassItem>) => {
      state.classes.push(action.payload);
    },

    updateClass: (state, action: PayloadAction<ClassItem>) => {
      const idx = state.classes.findIndex((c) => c.id === action.payload.id);
      if (idx !== -1) {
        state.classes[idx] = action.payload;
      }
    },

    deleteClass: (state, action: PayloadAction<string>) => {
      state.classes = state.classes.filter((c) => c.id !== action.payload);
    },

    // optional convenience: upsert (add or update)
    upsertClass: (state, action: PayloadAction<ClassItem>) => {
      const idx = state.classes.findIndex((c) => c.id === action.payload.id);
      if (idx === -1) state.classes.push(action.payload);
      else state.classes[idx] = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // optional: clear all classes
    clearClasses: (state) => {
      state.classes = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setClasses,
  addClass,
  updateClass,
  deleteClass,
  upsertClass,
  setLoading,
  setError,
  clearClasses,
} = classesSlice.actions;

export default classesSlice.reducer;

/**
 * Selectors (usage: useSelector(selectClasses) )
 */
export const selectClasses = (state: { classes: ClassesState }) =>
  state.classes.classes;

export const selectClassById = (id: string) => (state: { classes: ClassesState }) =>
  state.classes.classes.find((c) => c.id === id);

export const selectClassesLoading = (state: { classes: ClassesState }) =>
  state.classes.loading;
